import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Instagram, Linkedin, Twitter, FileText, Images } from 'lucide-react';

const navLinks = [
  { name: 'Home', href: '#hero' },
  { name: 'About', href: '#about' },
  { name: 'Work', href: '#work' },
  { name: 'Packages', href: '#packages' },
  { name: 'Gallery', href: '/gallery', isPage: true },
  { name: 'Contact', href: '#contact' },
];

export const Navigation = ({ onOpenQuestionnaire, showQuestionnaire }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isSubPage = window.location.pathname !== '/' && window.location.pathname !== '';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href, isPage) => {
    if (isPage) {
      window.location.href = href;
      return;
    }
    
    // If on a sub-page (like /gallery), navigate to homepage with hash
    if (isSubPage) {
      window.location.href = '/' + href;
      return;
    }
    
    if (href === '#hero') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        data-testid="navigation"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass py-3' : 'py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <a 
            href="/" 
            data-testid="nav-logo"
            className="text-xl font-bold tracking-tight"
            onClick={(e) => {
              if (showQuestionnaire) {
                e.preventDefault();
                // Do nothing, let parent handle it
              } else if (!isSubPage) {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
              // If on sub-page, let the default href="/" navigation happen
            }}
          >
            <span className="text-white">Kirit</span>
            <span className="text-[#D4AF37]">Sobti</span>
          </a>

          {/* Desktop Navigation */}
          {!showQuestionnaire && (
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  onClick={() => scrollToSection(link.href, link.isPage)}
                  className="text-sm text-white/70 hover:text-[#D4AF37] transition-colors duration-200 animated-underline"
                >
                  {link.name}
                </button>
              ))}
            </div>
          )}

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://instagram.com/businesswisdombykirit"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-instagram"
              className="text-white/60 hover:text-[#D4AF37] transition-colors"
            >
              <Instagram size={18} />
            </a>
            
            {/* HIGHLIGHTED QUESTIONNAIRE BUTTON */}
            <button
              data-testid="nav-questionnaire"
              onClick={onOpenQuestionnaire}
              className="relative px-4 py-2 rounded-full font-semibold text-sm overflow-hidden group"
              style={{
                background: 'linear-gradient(135deg, #D4AF37 0%, #F4D06F 50%, #D4AF37 100%)',
              }}
            >
              {/* Animated shine effect */}
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <span className="relative flex items-center gap-2 text-[#0A0A0A]">
                <FileText size={16} />
                Fill Questionnaire
              </span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setIsMobileMenuOpen(true)}
            className="lg:hidden text-white p-2"
          >
            <Menu size={24} />
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mobile-menu"
            data-testid="mobile-menu"
          >
            <button
              data-testid="mobile-menu-close"
              onClick={() => setIsMobileMenuOpen(false)}
              className="absolute top-6 right-6 text-white p-2"
            >
              <X size={28} />
            </button>
            
            <div className="flex flex-col items-center gap-6">
              {!showQuestionnaire && navLinks.map((link, index) => (
                <motion.button
                  key={link.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => scrollToSection(link.href, link.isPage)}
                  className="mobile-menu-link"
                >
                  {link.name}
                </motion.button>
              ))}
              
              {/* Highlighted Questionnaire Button in Mobile */}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onOpenQuestionnaire();
                }}
                className="px-6 py-3 rounded-full font-semibold text-[#0A0A0A] flex items-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #D4AF37 0%, #F4D06F 50%, #D4AF37 100%)',
                }}
              >
                <FileText size={18} />
                Fill Questionnaire
              </motion.button>
            </div>

            <div className="flex gap-6 mt-8">
              <a href="https://instagram.com/businesswisdombykirit" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#D4AF37]">
                <Instagram size={24} />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#D4AF37]">
                <Linkedin size={24} />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#D4AF37]">
                <Twitter size={24} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
